import { CyberTopologyNode } from '../../../models/cyber-topology.model';

/**
 * Maps a topology's own coordinate space onto the SVG viewBox, and back again.
 *
 * Authors place nodes in whatever units suit them; the diagram normalises. The editor needs
 * the inverse of exactly that mapping to turn a drop position back into author coordinates,
 * so both directions live here rather than being derived twice and drifting apart.
 */

export const VIEW_W = 100;
export const VIEW_H = 62;
export const PAD_X = 6;
export const PAD_Y = 5;
/**
 * Narrowest a node box may be, and the unit the layout is spaced in.
 *
 * Boxes size themselves to their label — see {@link nodeWidth} — so this is a floor rather than
 * the width: a short key like `PLC1` gets a box this wide and no narrower.
 */
export const NODE_W = 9;
export const NODE_H = 5;
export const LEAF_R = 1.4;

// ── Node box sizing ──────────────────────────────────────────────────────────

/** Matches `.node-label` in both node stylesheets; the box is sized to hold text this big. */
export const NODE_LABEL_FONT = 2.4;

/** Breathing room between the label and the box edge, per side. */
export const NODE_PAD_X = 1.6;

/** Widest a box may get. Past this, one verbose name would distort the whole drawing. */
export const NODE_MAX_W = 26;

/**
 * Average glyph advance as a fraction of the font size, for the bold label face.
 *
 * SVG cannot measure text without rendering it first, and a measure-then-relayout pass would make
 * the geometry depend on paint order in two components that today agree by construction. An
 * estimate is close enough for a diagram, and this one errs generous rather than tight.
 */
const AVG_CHAR_ADVANCE = 0.62;

/** Gap between the bottom of a box and the subtitle's baseline. */
export const NODE_SUB_GAP = 2.2;

/** Distance between label baselines — the label font with ordinary leading. */
export const NODE_LINE_H = 3;

/** Characters that fit on one line inside a box at its widest. */
export const NODE_MAX_LINE_CHARS = Math.floor(
  (NODE_MAX_W - 2 * NODE_PAD_X) / (NODE_LABEL_FONT * AVG_CHAR_ADVANCE),
);

/** Tallest a box may get, in lines. Past this a pasted sentence would dominate the drawing. */
export const NODE_MAX_LINES = 3;

/**
 * Width of a box holding `label`, between {@link NODE_W} and {@link NODE_MAX_W}.
 *
 * Driven by the longest line rather than the whole label, so a wrapped box is only as wide as it
 * needs to be instead of always sitting at the cap.
 */
export function nodeWidth(label: string): number {
  const longest = wrapNodeLabel(label).reduce((max, line) => Math.max(max, line.length), 0);
  const text = longest * NODE_LABEL_FONT * AVG_CHAR_ADVANCE + 2 * NODE_PAD_X;
  return Math.round(Math.min(NODE_MAX_W, Math.max(NODE_W, text)) * 100) / 100;
}

/** Height of a box holding `label`: one line is {@link NODE_H}, each extra line adds a line height. */
export function nodeHeight(label: string): number {
  const lines = Math.max(1, wrapNodeLabel(label).length);
  return NODE_H + (lines - 1) * NODE_LINE_H;
}

/**
 * `label` broken into the lines a box will draw.
 *
 * Greedy word wrap: a word moves to the next line whole, and only a single word too long for a
 * line of its own is split mid-word. Anything past {@link NODE_MAX_LINES} is dropped rather than
 * marked with an ellipsis — the full text is on the node's `<title>`, and a cut-off marker inside
 * the box reads as damage rather than as design.
 */
export function wrapNodeLabel(label: string): string[] {
  const text = (label ?? '').trim().replace(/\s+/g, ' ');
  if (!text) return [''];

  const lines: string[] = [];
  let line = '';

  const push = (): void => {
    lines.push(line);
    line = '';
  };

  for (const word of text.split(' ')) {
    let rest = word;

    // A word wider than a whole line cannot be placed by wrapping alone; break it where it
    // reaches the edge and carry the remainder on.
    while (rest.length > NODE_MAX_LINE_CHARS) {
      if (line) push();
      if (lines.length >= NODE_MAX_LINES) return lines;
      line = rest.slice(0, NODE_MAX_LINE_CHARS);
      rest = rest.slice(NODE_MAX_LINE_CHARS);
      push();
      if (lines.length >= NODE_MAX_LINES) return lines;
    }

    if (!rest) continue;

    const candidate = line ? `${line} ${rest}` : rest;
    if (candidate.length <= NODE_MAX_LINE_CHARS) {
      line = candidate;
      continue;
    }

    push();
    if (lines.length >= NODE_MAX_LINES) return lines;
    line = rest;
  }

  if (line) lines.push(line);
  return lines.slice(0, NODE_MAX_LINES);
}

/**
 * Magnification of a diagram inside its viewport. The SVG keeps its viewBox and simply
 * renders larger, so the surrounding container scrolls and no layout maths knows about zoom.
 * Shared by the read-only diagram and the editor, which behave identically here.
 */
export const ZOOM_MIN = 1;
export const ZOOM_MAX = 4;
export const ZOOM_STEP = 1.25;

export interface TopologyTransform {
  /** Author x -> viewBox x. */
  tx(x: number): number;
  /** Author y -> viewBox y (flipped, so up is up). */
  ty(y: number): number;
  /** viewBox x -> author x. */
  ix(x: number): number;
  /** viewBox y -> author y. */
  iy(y: number): number;
}

/** viewBox units per author unit. Fixed, so growing the canvas never rescales the drawing. */
export const AUTHOR_UNIT = (VIEW_W - 2 * PAD_X - NODE_W) / 10;

/** Where author (0, 0) sits — the same place {@link topologyTransform} puts it today. */
const ORIGIN_X = PAD_X + NODE_W / 2;
const BASE_Y = VIEW_H - PAD_Y;

/**
 * Author space pinned to the viewBox at a constant scale, whatever window is shown of it.
 *
 * The editor needs this rather than {@link topologyTransform}: normalising against the live
 * node set would shift every node as one is dragged, and would rescale the whole drawing as
 * the canvas grows. Here, growing the canvas adds room and moves nothing.
 */
export function fixedTransform(): TopologyTransform {
  return {
    tx: (x) => ORIGIN_X + x * AUTHOR_UNIT,
    ty: (y) => BASE_Y - y * AUTHOR_UNIT,
    ix: (x) => (x - ORIGIN_X) / AUTHOR_UNIT,
    iy: (y) => (BASE_Y - y) / AUTHOR_UNIT,
  };
}

/**
 * A diagram with one node, or none, has no span to normalise against. Falling back to a
 * unit span keeps the maths finite and puts a lone node in the middle rather than at NaN.
 */
export function topologyTransform(nodes: readonly CyberTopologyNode[]): TopologyTransform {
  const xs = nodes.map((n) => n.x);
  const ys = nodes.map((n) => n.y);
  const minX = xs.length > 0 ? Math.min(...xs) : 0;
  const maxX = xs.length > 0 ? Math.max(...xs) : 1;
  const minY = ys.length > 0 ? Math.min(...ys) : 0;
  const maxY = ys.length > 0 ? Math.max(...ys) : 1;
  const spanX = maxX - minX || 1;
  const spanY = maxY - minY || 1;

  // Positions are normalised to the span of node centres, so the room reserved for half a box on
  // each side has to be the largest box there is — otherwise the node holding the longest label
  // hangs outside the viewBox at whichever end it sits. A wrapped label makes that true
  // vertically as well as horizontally.
  const widest = largestBox(nodes, nodeWidth, NODE_W);
  const tallest = largestBox(nodes, nodeHeight, NODE_H);
  const innerW = VIEW_W - 2 * PAD_X - widest;
  const innerH = VIEW_H - 2 * PAD_Y - tallest;
  const originX = PAD_X + widest / 2;
  const baseY = VIEW_H - PAD_Y - tallest / 2;

  return {
    tx: (x) => originX + ((x - minX) / spanX) * innerW,
    ty: (y) => baseY - ((y - minY) / spanY) * innerH,
    ix: (x) => minX + ((x - originX) / innerW) * spanX,
    iy: (y) => minY + ((baseY - y) / innerH) * spanY,
  };
}

/** The largest box the set will draw. Leaves are circles and have no box to reserve room for. */
function largestBox(
  nodes: readonly CyberTopologyNode[],
  measure: (label: string) => number,
  floor: number,
): number {
  let largest = floor;
  for (const node of nodes) {
    if (node.kind === 'leaf') continue;
    largest = Math.max(largest, measure(node.label));
  }
  return largest;
}
